import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Inbox from "@/pages/Inbox";
import Leads from "@/pages/Leads";
import Pipeline from "@/pages/Pipeline";
import Contacts from "@/pages/Contacts";
import DealDetail from "@/pages/DealDetail";
import Projects from "@/pages/Projects";
import Calendar from "@/pages/Calendar";
import Scheduler from "@/pages/Scheduler";
import PublicBooking from "@/pages/PublicBooking";
import ManageBooking from "@/pages/ManageBooking";
import Chatbot from "@/pages/Chatbot";
import PublicChat from "@/pages/PublicChat";
import Invoices from "@/pages/Invoices";
import InvoiceEditor from "@/pages/InvoiceEditor";
import PublicInvoice from "@/pages/PublicInvoice";
import PublicReceipt from "@/pages/PublicReceipt";
import PublicStatement from "@/pages/PublicStatement";
import Quotations from "@/pages/Quotations";
import QuotationEditor from "@/pages/QuotationEditor";
import PublicQuote from "@/pages/PublicQuote";
import Products from "@/pages/Products";
import CostSummary from "@/pages/CostSummary";
import Analytics from "@/pages/Analytics";
import Divisions from "@/pages/Divisions";
import Settings from "@/pages/Settings";
import SuperAdmin from "@/pages/SuperAdmin";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;
  return <AppShell>{children}</AppShell>;
}

function SuperOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

function AuthGate({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<AuthGate><Login /></AuthGate>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/book/:slug" element={<PublicBooking />} />
          <Route path="/manage/:token" element={<ManageBooking />} />
          <Route path="/chat/:divisionId" element={<PublicChat />} />
          <Route path="/chat/agent/:agentSlug" element={<PublicChat />} />
          <Route path="/w/:widgetSlug" element={<PublicChat />} />
          <Route path="/inv/:slug" element={<PublicInvoice />} />
          <Route path="/receipt/:slug" element={<PublicReceipt />} />
          <Route path="/statement/:slug" element={<PublicStatement />} />
          <Route path="/quote/:slug" element={<PublicQuote />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          <Route path="/admin" element={<SuperOnly><SuperAdmin /></SuperOnly>} />

          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
          <Route path="/leads" element={<Protected><Leads /></Protected>} />
          <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
          <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
          <Route path="/projects" element={<Protected><Projects /></Protected>} />
          <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
          <Route path="/deals/:id" element={<Protected><DealDetail /></Protected>} />
          <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
          <Route path="/invoices/new" element={<Protected><InvoiceEditor /></Protected>} />
          <Route path="/invoices/:id" element={<Protected><InvoiceEditor /></Protected>} />
          <Route path="/quotations" element={<Protected><Quotations /></Protected>} />
          <Route path="/quotations/new" element={<Protected><QuotationEditor /></Protected>} />
          <Route path="/quotations/:id" element={<Protected><QuotationEditor /></Protected>} />
          <Route path="/products" element={<Protected><Products /></Protected>} />
          <Route path="/cost-summary" element={<Protected><CostSummary /></Protected>} />
          <Route path="/scheduler" element={<Protected><Scheduler /></Protected>} />
          <Route path="/chatbot" element={<Protected><Chatbot /></Protected>} />
          <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
          <Route path="/divisions" element={<Protected><Divisions /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
